import React, { useEffect, useState } from "react";
import { DashboardTravelRequest } from "@/lib/models/dashboard";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { Calendar, MapPin, Users, Euro, Clock, Mail, Phone, FileText, User, AlertTriangle } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatBudget, formatTravelDates, formatCreatedDate } from "@/components/utils/utils";
import { useLanguage } from "@/contexts/LanguageContext";

// Tipo union per supportare entrambi i modelli
type TravelRequestType = DashboardTravelRequest | TravelRequest;

// Helper per normalizzare i valori tra i due tipi
const normalize = (tr: TravelRequestType) => ({
    contactFullName: 'contactFullName' in tr ? tr.contactFullName : `${tr.contact_first_name} ${tr.contact_last_name}`.trim(),
    contactEmail: 'contactEmail' in tr ? tr.contactEmail : tr.contact_email,
    contactPhone: 'contactPhone' in tr ? tr.contactPhone : tr.contact_phone,
    departurePoint: 'departurePoint' in tr ? tr.departurePoint : tr.departure_point,
    travelStartDate: 'travelStartDate' in tr ? tr.travelStartDate : tr.travel_start_date,
    travelEndDate: 'travelEndDate' in tr ? tr.travelEndDate : tr.travel_end_date,
    travelDatesFlexible: 'travelDatesFlexible' in tr ? tr.travelDatesFlexible : tr.travel_dates_flexible,
    totalGuests: 'totalGuests' in tr ? tr.totalGuests : tr.total_guests,
    guestsData: 'guestsData' in tr ? tr.guestsData : tr.guests_data,
    budgetMin: 'budgetMin' in tr ? tr.budgetMin : tr.budget_min,
    budgetMax: 'budgetMax' in tr ? tr.budgetMax : tr.budget_max,
    budgetFlexible: 'budgetFlexible' in tr ? tr.budgetFlexible : tr.budget_flexible,
    needsTransportation: 'needsTransportation' in tr ? tr.needsTransportation : tr.needs_transportation,
    additionalNotes: 'additionalNotes' in tr ? tr.additionalNotes : tr.additional_notes,
    createdAt: 'createdAt' in tr ? tr.createdAt : tr.created_at,
    // Per assignedAdvisor e preferredAdvisor - DashboardTravelRequest ha oggetti, TravelRequest ha users_detail
    assignedAdvisorName: 'assignedAdvisor' in tr && tr.assignedAdvisor 
        ? tr.assignedAdvisor.fullName 
        : ('users_detail' in tr && tr.users_detail?.assigned) ? `${tr.users_detail.assigned.first_name} ${tr.users_detail.assigned.last_name}` : null,
    preferredAdvisorName: 'preferredAdvisor' in tr && tr.preferredAdvisor 
        ? tr.preferredAdvisor.fullName 
        : ('users_detail' in tr && tr.users_detail?.preferred) ? `${tr.users_detail.preferred.first_name} ${tr.users_detail.preferred.last_name}` : null,
    hasAssignedAdvisor: 'assignedAdvisor' in tr ? !!tr.assignedAdvisor : ('users_detail' in tr && !!tr.users_detail?.assigned),
    hasPreferredAdvisor: 'preferredAdvisor' in tr ? !!tr.preferredAdvisor : ('users_detail' in tr && !!tr.users_detail?.preferred),
});

interface TravelRequestDetailsProps {
    travelRequest: TravelRequestType;
}

export const TravelRequestDetails: React.FC<TravelRequestDetailsProps> = ({
    travelRequest,
}) => {
    const { t } = useLanguage();
    const data = normalize(travelRequest);
    
    const calculateExpiryDate = (createdAt: string) => {
        const created = new Date(createdAt);
        const expiry = new Date(created.getTime() + 48 * 60 * 60 * 1000); // 48 ore
        return expiry;
    };

    const calculateIsExpired = (status: string, createdAt: string) => {
        if (status !== 'pending') {
            return false;
        }
        const now = new Date();
        const expiry = calculateExpiryDate(createdAt);
        return now.getTime() > expiry.getTime();
    }

    return (
        <div className="py-6 px-6 space-y-6">
            {/* Contatti - Sezione a 3 colonne */}
            <div className="space-y-2 mb-5">
                <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-gray-600" />
                    <h4 className="font-semibold text-gray-900 text-sm">
                        {t('travelRequest.contacts')}
                    </h4>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 sm:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                        <Users className="h-3 w-3 text-gray-500" />
                        <span className="text-gray-700">
                            {data.contactFullName}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-gray-500" />
                        <span className="text-gray-700">{data.contactEmail}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Phone className="h-3 w-3 text-gray-500" />
                        <span className="text-gray-700">{data.contactPhone}</span>
                    </div>
                </div>
            </div>

            {/* Sezione principale - Destinazioni, Date e Gruppo */}
            <div className="grid grid-cols-1 lg:grid-cols-3 sm:grid-cols-2 gap-4 mb-5">
                {/* Destinazioni */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('itinerary.destinations')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">
                        {travelRequest.destinations.length > 0
                            ? travelRequest.destinations.join(", ")
                            : t('travelRequest.destinationTBD')
                        }
                    </p>
                    {data.departurePoint && (
                        <p className="text-xs text-gray-600">
                            {t('travelRequest.departureFrom')}: {data.departurePoint}
                        </p>
                    )}
                </div>

                {/* Date di viaggio */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.travelDates')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">
                        {formatTravelDates(data.travelStartDate, data.travelEndDate, {
                            fallbackText: t('travelRequest.datesToBeDefined'),
                            showYear: true
                        })}
                    </p>
                    {data.travelDatesFlexible && (
                        <p className="text-xs text-blue-600">
                            {t('travelRequest.flexibleDates')}
                        </p>
                    )}
                </div>

                {/* Dettagli gruppo */}
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.group')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">
                        {data.totalGuests} {data.totalGuests === 1 ? t('travelRequest.person') : t('travelRequest.people')}
                    </p>
                    {data.guestsData && data.guestsData.length > 0 && (
                        <div className="space-y-1">
                            <p className="text-xs text-gray-600">{t('travelRequest.guestsDetails')}:</p>
                            <div className="flex flex-wrap gap-1">
                                {data.guestsData.map((guest) => (
                                    <span
                                        key={guest.id}
                                        className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
                                    >
                                        {guest.age} {t('travelRequest.years')}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Sezione Budget, Trasporto e Designer */}
            <div className="grid grid-cols-1 lg:grid-cols-3 sm:grid-cols-2 gap-4 mb-5">
                {/* Budget */}
                <div className="space-y-2 pb-4">
                    <div className="flex items-center gap-2">
                        <Euro className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.budget')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">
                        {formatBudget(data.budgetMin, data.budgetMax)}
                    </p>
                    {data.budgetFlexible && (
                        <p className="text-xs text-blue-600">
                            {t('travelRequest.flexibleBudget')}
                        </p>
                    )}
                </div>

                {/* Trasporto */}
                <div className="space-y-2 pb-4">
                    <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.transportation')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm">
                        {data.needsTransportation ? t('travelRequest.transportationRequired') : t('travelRequest.transportationNotRequired')}
                    </p>
                </div>

                {/* Designer: mostra sia assegnato che preferito risolti dagli id */}
                <div className="space-y-2 pb-4">
                    <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.designer')}</h4>

                    {/* Designer assegnato */}
                    {data.hasAssignedAdvisor ? (
                        <p className="text-gray-700 text-sm">
                            {t('travelRequest.assigned')}: {data.assignedAdvisorName}
                        </p>
                    ) : (
                        <>
                            <p className="text-gray-700 text-sm">{t('travelRequest.notAssigned')}</p>
                            {data.hasPreferredAdvisor ? (
                                <p className="text-gray-700 text-sm">
                                    {t('travelRequest.preferred')}: {data.preferredAdvisorName}
                                </p>
                            ) : (
                                <p className="text-gray-700 text-sm">{t('travelRequest.noPreference')}</p>
                            )}
                        </>
                    )}

                </div>


                {/* Interessi - Solo se presenti */}
                {travelRequest.interests && travelRequest.interests.length > 0 && (
                    <div className="space-y-2 pb-4">
                        <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-600" />
                            <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.interests')}</h4>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {travelRequest.interests.map((interest, index) => (
                                <span
                                    key={index}
                                    className="px-2 py-0.5 bg-gray-100 text-gray-800 rounded text-xs"
                                >
                                    {interest}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>


            {/* Note aggiuntive - Solo se presenti */}
            {data.additionalNotes && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-600" />
                        <h4 className="font-semibold text-gray-900 text-sm">{t('travelRequest.additionalNotes')}</h4>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">{data.additionalNotes}</p>
                </div>
            )}


            {/* Alert per la scadenza */}
            {travelRequest.isPending && (
                <Alert variant="info" className="mt-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="font-medium">
                            {t('travelRequest.expiresOn')} {formatCreatedDate(calculateExpiryDate(data.createdAt).toISOString(), { format: 'long' })} {t('travelRequest.at')} {calculateExpiryDate(data.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="text-sm mt-1 opacity-80">
                            {t('travelRequest.expiryMessage')}
                        </div>
                    </AlertDescription>
                </Alert>
            )}
            {calculateIsExpired(travelRequest.status, data.createdAt) && (
                <Alert variant="destructive" className="mt-4">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="font-medium">
                            {t('travelRequest.expiredOn')} {formatCreatedDate(calculateExpiryDate(data.createdAt).toISOString(), { format: 'long' })} {t('travelRequest.at')} {calculateExpiryDate(data.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div className="text-sm mt-1 opacity-80">
                            {t('travelRequest.expiredMessage')}
                        </div>
                    </AlertDescription>
                </Alert>
            )}
        </div>
    );
};

